import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'

import type { MyRestaurant } from '@/bindings/MyRestaurant'
import { AccentScope } from '@/components/accent'
import { Badge } from '@/components/badge'
import { Button } from '@/components/button'
import { buttonClass } from '@/components/button-class'
import { FormError } from '@/components/form-error'
import { Page } from '@/components/layout'
import { myRestaurantsQuery } from '@/features/back-office/api'
import { useT } from '@/lib/i18n/translate'
import { ROLE_LABELS } from '@/lib/roles'

/**
 * Every restaurant the user holds a role in (E6). Each row opens the restaurant's
 * landing page, where the role's way in (manage, or a branch) is offered. One
 * self-serve restaurant per account (K1): the button goes once a superuser seat
 * is held, deactivated or not — the backend is the authority, this only avoids a
 * dead button.
 */
export function MyRestaurantsPage() {
  const t = useT()
  const mine = useQuery(myRestaurantsQuery())
  const ownsOne = mine.data?.items.some((restaurant) => restaurant.role === 'superuser') ?? false

  return (
    <Page width="md" title={t('backOffice.title')}>
      {mine.data ? (
        mine.data.items.length === 0 ? (
          <p className="text-muted text-sm">{t('backOffice.empty')}</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {mine.data.items.map((restaurant) => (
              <li key={restaurant.id}>
                <RestaurantRow restaurant={restaurant} />
              </li>
            ))}
          </ul>
        )
      ) : mine.error ? (
        <div className="flex flex-col items-start gap-3">
          <FormError error={mine.error} />
          <Button variant="secondary" onClick={() => void mine.refetch()}>
            {t('common.retry')}
          </Button>
        </div>
      ) : (
        <p className="text-muted text-sm">{t('common.loading')}</p>
      )}
      {/* Below the list: staff come for their restaurants, not to create one (2026-10-05). */}
      <div className="mt-6 flex flex-col items-start gap-2">
        {ownsOne ? (
          <p className="text-muted text-sm">{t('backOffice.createCap')}</p>
        ) : (
          mine.data?.items.length === 0 && <p className="text-muted text-sm">{t('backOffice.emptyCreate')}</p>
        )}
        <div className="flex flex-wrap gap-3">
          {mine.data && !ownsOne && (
            <Link to="/app/restaurants/new" className={buttonClass()}>
              {t('backOffice.create')}
            </Link>
          )}
          {/* Here, not in the avatar menu: only restaurant people need it. */}
          <Link to="/app/guide/$guide" params={{ guide: 'create-restaurant' }} className={buttonClass('secondary')}>
            {t('guide.title')}
          </Link>
        </div>
      </div>
    </Page>
  )
}

function RestaurantRow({ restaurant }: { restaurant: MyRestaurant }) {
  const t = useT()
  const body = (
    <>
      <span aria-hidden className="bg-accent size-4 shrink-0 rounded-full" />
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="truncate font-medium">{restaurant.name}</span>
          <Badge tone="muted">{t(ROLE_LABELS[restaurant.role])}</Badge>
          {restaurant.deactivated_at && <Badge tone="warning">{t('restaurants.deactivated')}</Badge>}
        </span>
        <span className="text-muted block text-sm">/{restaurant.slug}</span>
      </span>
    </>
  )
  const row = 'border-border flex min-h-tap items-center gap-3 rounded-lg border p-3'

  const params = { restaurantSlug: restaurant.slug }
  return (
    <AccentScope color={restaurant.color}>
      {/* A deactivated restaurant's public page is a notice (H1); its back office shows the lock (2026-09-12). */}
      {restaurant.deactivated_at ? (
        <Link to="/$restaurantSlug/manage/branches" params={params} className={`${row} hover:bg-accent-soft`}>
          {body}
        </Link>
      ) : (
        <Link to="/$restaurantSlug" params={params} className={`${row} hover:bg-accent-soft`}>
          {body}
        </Link>
      )}
    </AccentScope>
  )
}
